import React, { useState, useEffect } from 'react';
import { Button, Progress, Typography, Space, Tag } from 'antd';
import { 
  DeleteOutlined, 
  FileImageOutlined, 
  FileTextOutlined, 
  SoundOutlined,
  EyeOutlined,
  LoadingOutlined 
} from '@ant-design/icons';
import styled from 'styled-components';
import { FileUploadItem } from '../hooks/useFileUpload';

const { Text } = Typography;

// 样式组件
const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  max-height: 300px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d4edda;
    border-radius: 2px;
  }
`;

const PreviewItem = styled.div<{ $status: FileUploadItem['status'] }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid ${props => {
    switch (props.$status) {
      case 'success': return '#d4edda';
      case 'error': return '#f8d7da';
      case 'uploading': return '#cce5ff';
      default: return '#e9ecef';
    }
  }};
  border-radius: 8px;
  background: ${props => {
    switch (props.$status) {
      case 'success': return '#f8fff9';
      case 'error': return '#fff5f5';
      case 'uploading': return '#f0f8ff';
      default: return '#ffffff';
    }
  }};
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const PreviewContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const FileIcon = styled.div<{ $type: string }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: ${props => {
    switch (props.$type) {
      case 'image': return '#e8f5e9';
      case 'audio': return '#fff3e0';
      case 'document': return '#e3f2fd';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'image': return '#4caf50';
      case 'audio': return '#ff9800';
      case 'document': return '#2196f3';
      default: return '#757575';
    }
  }};
  font-size: 18px;
  flex-shrink: 0;
`;

const ImagePreview = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
`;

const AudioPreview = styled.audio`
  width: 200px;
  height: 30px;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FileName = styled(Text)`
  font-weight: 500;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

const FileDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const FileSize = styled(Text)`
  font-size: 12px;
  color: #666;
`;

const FileType = styled(Tag)`
  font-size: 10px;
  padding: 0 4px;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const RemoveButton = styled(Button)`
  &.ant-btn {
    width: 24px;
    height: 24px;
    min-width: 24px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: none;
    background: #ff4d4f;
    color: white;
    
    &:hover {
      background: #ff7875 !important;
      border-color: #ff7875 !important;
      color: white !important;
    }
  }
`;

const ProgressWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  
  .ant-progress {
    margin: 0;
  }
  
  .ant-progress-line {
    margin: 0;
  }
`;

// 组件接口
interface FilePreviewProps {
  files: FileUploadItem[];
  onRemove: (fileId: string) => void;
  onPreview?: (file: FileUploadItem) => void;
  showProgress?: boolean;
  compact?: boolean;
}

/**
 * 格式化文件大小
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * 获取文件类型图标
 */
const getFileIcon = (type: string, file: FileUploadItem) => {
  switch (type) {
    case 'image':
      return file.url ? (
        <ImagePreview src={file.url} alt={file.file.name} />
      ) : (
        <FileIcon $type={type}>
          <FileImageOutlined />
        </FileIcon>
      );
    case 'audio':
      return (
        <FileIcon $type={type}>
          <SoundOutlined />
        </FileIcon>
      );
    case 'document':
      return (
        <FileIcon $type={type}>
          <FileTextOutlined />
        </FileIcon>
      );
    default:
      return (
        <FileIcon $type={type}>
          <FileTextOutlined />
        </FileIcon>
      );
  }
};

/**
 * 获取状态颜色
 */
const getStatusColor = (status: FileUploadItem['status']) => {
  switch (status) {
    case 'success': return 'success';
    case 'error': return 'error';
    case 'uploading': return 'processing';
    default: return 'default';
  }
};

/**
 * 文件预览组件
 */
const FilePreview: React.FC<FilePreviewProps> = ({
  files,
  onRemove,
  onPreview,
  showProgress = true,
  compact = false
}) => {
  const [audioDurations, setAudioDurations] = useState<Record<string, number>>({});

  // 获取音频时长
  useEffect(() => {
    const loadAudioDurations = async () => {
      const audioFiles = files.filter(f => f.type === 'audio');
      const durations: Record<string, number> = {};
      
      for (const file of audioFiles) {
        try {
          const audio = document.createElement('audio');
          audio.preload = 'metadata';
          
          const duration = await new Promise<number>((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Timeout')), 5000);
            
            audio.onloadedmetadata = () => {
              clearTimeout(timeout);
              resolve(audio.duration);
            };
            
            audio.onerror = () => {
              clearTimeout(timeout);
              reject(new Error('Failed to load'));
            };
            
            if (file.url) {
              audio.src = file.url;
            }
          });
          
          if (!isNaN(duration) && duration > 0) {
            durations[file.id] = duration;
          }
        } catch (error) {
          console.warn(`无法获取音频时长: ${file.file.name}`, error);
        }
      }
      
      setAudioDurations(prev => ({ ...prev, ...durations }));
    };

    loadAudioDurations();
  }, [files]);

  if (files.length === 0) {
    return null;
  }

  return (
    <PreviewContainer>
      {files.map((file) => (
        <PreviewItem key={file.id} $status={file.status}>
          <PreviewContent>
            {getFileIcon(file.type, file)}
            
            <FileInfo>
              <FileName>{file.file.name}</FileName>
              <FileDetails>
                <FileSize>{formatFileSize(file.file.size)}</FileSize>
                <FileType color={getStatusColor(file.status)}>
                  {file.type.toUpperCase()}
                </FileType>
                
                {file.type === 'audio' && audioDurations[file.id] && (
                  <FileSize>
                    {Math.floor(audioDurations[file.id] / 60)}:
                    {Math.floor(audioDurations[file.id] % 60).toString().padStart(2, '0')}
                  </FileSize>
                )}
                
                {file.status === 'uploading' && (
                  <FileSize>
                    <LoadingOutlined style={{ marginRight: 4 }} />
                    上传中...
                  </FileSize>
                )}
                
                {file.status === 'error' && (
                  <FileSize style={{ color: '#ff4d4f' }}>
                    上传失败
                  </FileSize>
                )}
                
                {file.status === 'success' && (
                  <FileSize style={{ color: '#52c41a' }}>
                    已完成
                  </FileSize>
                )}
              </FileDetails>
              
              {/* 音频预览播放器 */}
              {file.type === 'audio' && file.url && !compact && (
                <div style={{ marginTop: 4 }}>
                  <AudioPreview 
                    controls 
                    preload="metadata"
                    src={file.url}
                  >
                    您的浏览器不支持音频播放
                  </AudioPreview>
                </div>
              )}
            </FileInfo>
          </PreviewContent>
          
          <ActionButtons>
            {onPreview && file.type === 'image' && (
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => onPreview(file)}
                title="预览"
              />
            )}
            
            <RemoveButton
              icon={<DeleteOutlined style={{ fontSize: 12 }} />}
              onClick={() => onRemove(file.id)}
              title="删除文件"
            />
          </ActionButtons>
          
          {/* 上传进度条 */}
          {showProgress && file.status === 'uploading' && file.progress !== undefined && (
            <ProgressWrapper>
              <Progress 
                percent={file.progress} 
                size="small"
                showInfo={false}
                strokeColor="#1890ff"
              />
            </ProgressWrapper>
          )}
        </PreviewItem>
      ))}
    </PreviewContainer>
  );
};

export default FilePreview; 